import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import api from '../services/api';

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const fmtInt = (value) => number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtMoney = (value) =>
  `$${number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
const normalize = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const th = {
  padding: '9px 10px',
  borderBottom: '1px solid #d8dde6',
  background: '#f4f6f8',
  fontWeight: 700,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 1
};
const td = {
  padding: '8px 10px',
  borderBottom: '1px solid #edf0f4',
  verticalAlign: 'top'
};
const tdNumber = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ModalReportesVentas({ show, onClose, desde, hasta, onExportarDinamico }) {
  const [tipoReporte, setTipoReporte] = useState('producto');
  const [rowsProducto, setRowsProducto] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [idproducto, setIdproducto] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const cargarProductos = useCallback(async () => {
    if (!desde || !hasta) return;
    setCargando(true);
    try {
      const { data } = await api.get('/api/reportes/producto-clientes', {
        params: { desde, hasta }
      });
      setRowsProducto(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error cargando reporte por producto:', err);
      alert(err?.response?.data?.error || 'No se pudo cargar el reporte por producto.');
      setRowsProducto([]);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    if (show && tipoReporte === 'producto') cargarProductos();
  }, [show, tipoReporte, cargarProductos]);

  useEffect(() => {
    if (!show) {
      setIdproducto('');
      setBusqueda('');
    }
  }, [show]);

  const productos = useMemo(() => {
    const map = new Map();
    rowsProducto.forEach((row) => {
      const id = String(row.idproducto ?? '0');
      if (!map.has(id)) map.set(id, row.producto || 'SIN PRODUCTO');
    });
    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [rowsProducto]);

  const rowsFiltradas = useMemo(() => {
    const query = normalize(busqueda).trim();
    return rowsProducto.filter((row) => {
      if (idproducto && String(row.idproducto) !== String(idproducto)) return false;
      if (!query) return true;
      return normalize(
        `${row.producto} ${row.cliente_principal} ${row.subcliente}`
      ).includes(query);
    });
  }, [busqueda, idproducto, rowsProducto]);

  const totals = useMemo(
    () =>
      rowsFiltradas.reduce(
        (acc, row) => {
          acc.tallos += number(row.tallos);
          acc.facturas += number(row.facturas);
          acc.compra += number(row.compra);
          acc.venta += number(row.venta);
          acc.utilidad += number(row.utilidad);
          return acc;
        },
        { tallos: 0, facturas: 0, compra: 0, venta: 0, utilidad: 0 }
      ),
    [rowsFiltradas]
  );

  const descargarPaisOrigen = async () => {
    try {
      setDescargando(true);
      const { data } = await api.get('/api/reportes/fulls-pais-origen/excel', {
        params: { desde, hasta },
        responseType: 'blob'
      });
      downloadBlob(
        new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        `resumen_fulls_pais_destino_clientes_${desde}_${hasta}.xlsx`
      );
    } catch (err) {
      console.error('Error descargando resumen por pais de destino y cliente:', err);
      alert(err?.response?.data?.error || 'No se pudo descargar el reporte por país de destino y cliente.');
    } finally {
      setDescargando(false);
    }
  };

  const descargarProductoPdf = async () => {
    if (!rowsFiltradas.length) {
      alert('No hay datos para exportar.');
      return;
    }
    try {
      setDescargando(true);
      const params = { desde, hasta };
      if (idproducto) params.idproducto = idproducto;
      if (busqueda.trim()) params.q = busqueda.trim();
      const { data } = await api.get('/api/reportes/producto-clientes/pdf', {
        params,
        responseType: 'blob'
      });
      downloadBlob(
        new Blob([data], { type: 'application/pdf' }),
        `reporte_ventas_por_producto_${desde}_${hasta}.pdf`
      );
    } catch (err) {
      console.error('Error descargando reporte por producto:', err);
      alert(err?.response?.data?.error || 'No se pudo descargar el PDF por producto.');
    } finally {
      setDescargando(false);
    }
  };

  const exportarDinamico = async () => {
    try {
      setDescargando(true);
      await onExportarDinamico?.();
    } finally {
      setDescargando(false);
    }
  };

  const accionPrincipal = () => {
    if (tipoReporte === 'pais_origen') return descargarPaisOrigen();
    if (tipoReporte === 'dinamico') return exportarDinamico();
    return descargarProductoPdf();
  };

  const etiquetaAccion =
    tipoReporte === 'pais_origen'
      ? 'Descargar Excel'
      : tipoReporte === 'dinamico'
        ? 'Exportar PDF actual'
        : 'Descargar PDF';

  return (
    <Dialog open={show} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>Reportes de ventas</DialogTitle>
      <DialogContent dividers>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'end' }}
          sx={{ mb: 2 }}
        >
          <TextField
            select
            label="Tipo de reporte"
            size="small"
            value={tipoReporte}
            onChange={(event) => setTipoReporte(event.target.value)}
            sx={{ minWidth: 320 }}
          >
            <MenuItem value="producto">Ventas por producto y cliente</MenuItem>
            <MenuItem value="pais_origen">Fulls por país de destino y cliente</MenuItem>
            <MenuItem value="dinamico">Reporte dinámico actual</MenuItem>
          </TextField>

          <Box sx={{ px: 1, minWidth: 260 }}>
            <Typography variant="caption" color="text.secondary">
              Rango activo
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {desde} a {hasta}
            </Typography>
          </Box>

          {tipoReporte === 'producto' && (
            <>
              <TextField
                select
                label="Producto"
                size="small"
                value={idproducto}
                onChange={(event) => setIdproducto(event.target.value)}
                sx={{ minWidth: 250 }}
              >
                <MenuItem value="">Todos los productos</MenuItem>
                {productos.map((producto) => (
                  <MenuItem key={producto.id} value={producto.id}>
                    {producto.nombre}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Buscar cliente"
                size="small"
                value={busqueda}
                onChange={(event) => setBusqueda(event.target.value)}
                sx={{ minWidth: 240 }}
              />
              <Button variant="outlined" onClick={cargarProductos} disabled={cargando}>
                Actualizar
              </Button>
            </>
          )}
        </Stack>

        {cargando && <LinearProgress sx={{ mb: 1.5 }} />}

        {tipoReporte === 'producto' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Consulta cuánto se vendió de cada producto, a qué cliente, sus tallos, compra, venta y
              utilidad.
            </Typography>
            <Box sx={{ maxHeight: 520, overflow: 'auto', border: '1px solid #e2e7ed' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={th}>Producto</th>
                    <th style={th}>Cliente principal</th>
                    <th style={th}>Subcliente</th>
                    <th style={{ ...th, textAlign: 'right' }}>Tallos</th>
                    <th style={{ ...th, textAlign: 'right' }}>Facturas</th>
                    <th style={{ ...th, textAlign: 'right' }}>P. compra</th>
                    <th style={{ ...th, textAlign: 'right' }}>P. venta</th>
                    <th style={{ ...th, textAlign: 'right' }}>Utilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsFiltradas.map((row) => (
                    <tr key={`${row.idproducto}-${row.idcliente}`}>
                      <td style={td}>{row.producto || '-'}</td>
                      <td style={td}>{row.cliente_principal || '-'}</td>
                      <td style={td}>{row.subcliente || '-'}</td>
                      <td style={tdNumber}>{fmtInt(row.tallos)}</td>
                      <td style={tdNumber}>{fmtInt(row.facturas)}</td>
                      <td style={tdNumber}>{fmtMoney(row.compra)}</td>
                      <td style={tdNumber}>{fmtMoney(row.venta)}</td>
                      <td style={tdNumber}>{fmtMoney(row.utilidad)}</td>
                    </tr>
                  ))}
                  {!cargando && rowsFiltradas.length === 0 && (
                    <tr>
                      <td style={{ ...td, textAlign: 'center' }} colSpan={8}>
                        No hay datos para el rango o filtro seleccionado.
                      </td>
                    </tr>
                  )}
                </tbody>
                {rowsFiltradas.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#e9eff5', fontWeight: 700 }}>
                      <td style={{ ...td, textAlign: 'right' }} colSpan={3}>
                        Totales
                      </td>
                      <td style={tdNumber}>{fmtInt(totals.tallos)}</td>
                      <td style={tdNumber}>{fmtInt(totals.facturas)}</td>
                      <td style={tdNumber}>{fmtMoney(totals.compra)}</td>
                      <td style={tdNumber}>{fmtMoney(totals.venta)}</td>
                      <td style={tdNumber}>{fmtMoney(totals.utilidad)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </Box>
          </>
        )}

        {tipoReporte === 'pais_origen' && (
          <Box sx={{ p: 2, bgcolor: '#f7faf8', border: '1px solid #dce9df', borderRadius: 1 }}>
            <Typography variant="h6">Fulls por país de destino y cliente</Typography>
            <Typography variant="body2" color="text.secondary">
              Descarga el resumen de cajas full agrupado por país de destino y cliente en formato Excel.
            </Typography>
          </Box>
        )}

        {tipoReporte === 'dinamico' && (
          <Box sx={{ p: 2, bgcolor: '#f5f8fc', border: '1px solid #d9e4f0', borderRadius: 1 }}>
            <Typography variant="h6">Reporte dinámico actual</Typography>
            <Typography variant="body2" color="text.secondary">
              Exporta a PDF la gráfica y las filas visibles con los filtros, agrupaciones y columnas
              aplicados en la pantalla.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="contained" onClick={accionPrincipal} disabled={cargando || descargando}>
          {descargando ? 'Generando...' : etiquetaAccion}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
